import {
  otherSide,
  type FortState,
  type GameState,
  type ShipClass,
  type ShipState,
} from '@brig/rules-engine';
import type { ShipTokenState } from './ShipToken.tsx';

export type ShipStatus = 'sunk' | 'repairing' | 'anchored' | 'repairingFort' | 'ready';

/**
 * A docked ship rides at anchor in its own port; while it still has holes it is under repair.
 * A ship in a fort's repair crew is shown with the hammer too.
 */
export function shipStatus(ship: ShipState): ShipStatus {
  if (ship.sunk) return 'sunk';
  if (ship.dockedOnTurn !== null) return ship.damage > 0 ? 'repairing' : 'anchored';
  if (ship.repairingFort !== null) return 'repairingFort';
  return 'ready';
}

/**
 * The side to act is done with this ship's move for the turn, and the board fades it: it has
 * moved, it is in dock or on a fort's repair crew, or it sailed with the squadron but the wind
 * leaves it nowhere to go (a sailing ship in calm, R-MOVE-04, or no room on any ray).
 * `movable` is the squadron ships that may still move.
 */
export function isSpent(ship: ShipState, state: GameState, movable: ReadonlySet<string>): boolean {
  if (ship.side !== state.turn.side || ship.sunk) return false;
  if (ship.moved || ship.dockedOnTurn !== null || ship.repairingFort !== null) return true;
  return ship.inGroup && state.turn.phase === 'spun' && !movable.has(ship.id);
}

/**
 * A ship standing on the enemy Royal Port (R-ROYAL-04): the turns her side has held it so far
 * and the turns it needs to win; null anywhere else. The count is the side's, not the ship's:
 * the turn of entry does not count, and it resets once the port is left empty at a turn's end.
 */
export function royalHold(ship: ShipState, state: GameState): { held: number; needed: number } | null {
  if (ship.sunk || ship.cell !== state.map.royalPorts[otherSide(ship.side)]) return null;
  return { held: state.holdCount[ship.side], needed: state.ruleset.royalPortHoldTurns };
}

/**
 * Own turns until a docked ship has no holes left, counting the one under way: a hole is
 * mended at the end of each own turn but the turn it docked (R-SREP-03).
 */
export function dockTurnsLeft(ship: ShipState, turn: number): number {
  return ship.damage + (ship.dockedOnTurn === turn ? 1 : 0);
}

/** The same for a fort under repair, `perTurn` hits a turn but the first (R-FREP-03). */
export function fortRepairTurnsLeft(fort: FortState, perTurn: number, turn: number): number {
  return Math.ceil(fort.hits / perTurn) + (fort.repair?.startedOnTurn === turn ? 1 : 0);
}

/** The parts of a token's state that follow from the game state alone (not from the UI). */
export function tokenStateOf(ship: ShipState, cls: ShipClass): ShipTokenState {
  const status = shipStatus(ship);
  return {
    damage: ship.damage,
    sinksAt: cls.sinksAt,
    sunk: ship.sunk,
    anchored: status === 'anchored',
    repairing: status === 'repairing' || status === 'repairingFort',
  };
}
